import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useBookingActions = () => {
  const { toast } = useToast();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleBookingAction = async (
    bookingId: string, 
    action: 'approve' | 'reject',
    booking: any
  ) => {
    try {
      setProcessingId(bookingId);
      
      const newStatus = action === 'approve' ? 'confirmed' : 'rejected';
      
      const { error: updateError } = await supabase
        .from('bookings')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (updateError) throw updateError;

      // Create notification for the guest with correct notification type
      if (booking) {
        await supabase.from('notifications').insert({
          user_id: booking.user_id,
          title: `Booking ${action === 'approve' ? 'Approved' : 'Rejected'}`,
          message: `Your booking for ${booking.property?.title} has been ${action === 'approve' ? 'approved' : 'rejected'}.`,
          type: action === 'approve' ? 'booking_confirmed' : 'booking_rejected'
        });
      }

      toast({
        title: `Booking ${action}d`,
        description: `Successfully ${action}d the booking request`,
      });

      // Invalidate and refetch the bookings query
      await queryClient.invalidateQueries({ queryKey: ['pending-bookings'] });
    } catch (error) {
      console.error('Booking action error:', error);
      toast({
        title: "Error",
        description: `Failed to ${action} booking. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  return { handleBookingAction, processingId };
};